'use client';

import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import LoadingSpinner from '@/components/LoadingSpinner';

// Dynamic imports for heavy interactive components
const DynamicHeavyChart = dynamic(() => import('@/components/HeavyChart'), {
  loading: () => <LoadingSpinner message="Loading Chart..." />,
  ssr: false
});

const DynamicHeavyCalculator = dynamic(() => import('@/components/HeavyCalculator'), {
  loading: () => <LoadingSpinner message="Loading Calculator..." />,
  ssr: false
});

const DynamicHeavyTable = dynamic(() => import('@/components/HeavyTable'), {
  loading: () => <LoadingSpinner message="Loading Table..." />,
  ssr: false
});

type ViewMode = 'dashboard' | 'analytics' | 'reports' | null;
type UserRole = 'admin' | 'user' | 'guest';

const ConditionalClientComponent: React.FC = () => {
  const [currentView, setCurrentView] = useState<ViewMode>(null);
  const [userRole, setUserRole] = useState<UserRole>('user');
  const [showAdvanced, setShowAdvanced] = useState(false);

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg border">
      <div className="flex items-center gap-2 mb-4">
        <h3 className="text-lg font-semibold text-gray-800">
          Conditional Client Component
        </h3>
        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
          Client Component
        </span>
      </div>

      {/* User controls */}
      <div className="mb-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            User Role:
          </label>
          <select 
            value={userRole} 
            onChange={(e) => setUserRole(e.target.value as UserRole)}
            className="border rounded px-3 py-2 text-sm"
          >
            <option value="guest">Guest</option>
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>
        </div>

        {userRole === 'admin' && (
          <div>
            <label className="flex items-center gap-2 text-sm">
              <input 
                type="checkbox" 
                checked={showAdvanced}
                onChange={(e) => setShowAdvanced(e.target.checked)}
              />
              Show Advanced Features
            </label>
          </div>
        )}
      </div>

      {/* Dynamic view selection */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Choose a view to load:</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <button
            onClick={() => setCurrentView('dashboard')}
            className={`p-3 rounded-lg border transition-colors ${
              currentView === 'dashboard' 
                ? 'bg-blue-50 border-blue-300 text-blue-800' 
                : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
            }`}
          >
            📊 Dashboard
          </button>

          {(userRole === 'user' || userRole === 'admin') && (
            <button
              onClick={() => setCurrentView('analytics')}
              className={`p-3 rounded-lg border transition-colors ${
                currentView === 'analytics' 
                  ? 'bg-green-50 border-green-300 text-green-800' 
                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
              }`}
            >
              📈 Analytics
            </button>
          )}

          {userRole === 'admin' && (
            <button
              onClick={() => setCurrentView('reports')}
              className={`p-3 rounded-lg border transition-colors ${
                currentView === 'reports' 
                  ? 'bg-purple-50 border-purple-300 text-purple-800' 
                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
              }`}
            >
              📋 Reports
            </button>
          )}
        </div>
      </div>

      {/* Conditionally render heavy components */}
      <div className="min-h-[200px]">
        {currentView === 'dashboard' && (
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold text-blue-800 mb-2">Dashboard View</h4>
              <p className="text-sm text-blue-700">Loading interactive chart component...</p>
            </div>
            <DynamicHeavyChart title="Dashboard Chart (Dynamic)" />
          </div>
        )}

        {currentView === 'analytics' && (userRole === 'user' || userRole === 'admin') && (
          <div className="space-y-4">
            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold text-green-800 mb-2">Analytics View</h4>
              <p className="text-sm text-green-700">Loading heavy calculation component...</p>
            </div>
            <DynamicHeavyCalculator title="Analytics Calculator (Dynamic)" />
          </div>
        )}

        {currentView === 'reports' && userRole === 'admin' && (
          <div className="space-y-4">
            <div className="p-4 bg-purple-50 rounded-lg">
              <h4 className="font-semibold text-purple-800 mb-2">
                Reports View {showAdvanced && '(Advanced)'}
              </h4>
              <p className="text-sm text-purple-700">Loading data table component...</p>
            </div>
            <DynamicHeavyTable title="Admin Reports Table (Dynamic)" />
            
            {showAdvanced && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <h5 className="font-semibold text-red-800 mb-2">🔧 Advanced Admin Tools</h5>
                <p className="text-sm text-red-700 mb-3">
                  These would be additional heavy components loaded only for advanced admin features.
                </p>
                <button 
                  className="text-xs bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                  onClick={() => alert('Advanced tools would load here with dynamic imports')}
                >
                  Load Advanced Tools (Would be Dynamic)
                </button>
              </div>
            )}
          </div>
        )}

        {!currentView && (
          <div className="flex items-center justify-center h-32 bg-gray-50 rounded-lg">
            <p className="text-gray-500">Select a view above to see dynamic loading in action</p>
          </div>
        )}
      </div>

      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <h4 className="font-semibold text-blue-800 mb-2">🔵 Dynamic Import Benefits Here:</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Heavy components only load when user selects them</li>
          <li>• Role-based access controls which components can load</li>
          <li>• Interactive state management requires client-side code</li>
          <li>• Each component is a separate chunk, loaded on-demand</li>
          <li>• Better UX with loading states for heavy components</li>
        </ul>
      </div>
    </div>
  );
};

export default ConditionalClientComponent;
